import { useEffect, useState } from 'react';
import '@repo/brackets-viewer';
import { clubApi } from '@repo/api-client';
import { useRevalidator } from 'react-router';
import { MatchResultModal, type MatchResult } from './match-result-modal';
import { toast } from 'sonner';

interface BracketsRenderProps {
  data: any;
  categoryId: string;
  readonly?: boolean;
  tournament: Tournament;
  club: Club;
}

export const BracketsRender = ({
  data,
  categoryId,
  readonly = false,
  tournament,
  club,
}: BracketsRenderProps) => {
  const revalidator = useRevalidator();
  const [modalOpen, setModalOpen] = useState(false);
  const [currentMatch, setCurrentMatch] = useState<{
    match: any;
    opponent1: any;
    opponent2: any;
  } | null>(null);

  useEffect(() => {
    if (!data || !window.bracketsViewer) return;
    window.bracketsViewer.render(
      {
        stages: data.stage,
        matches: data.match,
        matchGames: data.match_game,
        participants: data.participant,
      },
      {
        separatedChildCountLabel: true,
        onMatchClick: async (match: any) => {
          if (readonly) return;
          if (match.status !== 2) {
            if (match.status === 4) {
              toast.error('El partido ya ha sido jugado');
              return;
            }
            toast.error('El partido no está listo para ser jugado');
            return;
          }
          // Validate that match has both opponents
          if (!match.opponent1 || !match.opponent2) {
            toast.error('El partido no tiene ambos oponentes definidos');
            return;
          }
          // Find participants - these can be undefined if not found
          const opponent1 = data.participant.find((p: any) => p.id === match.opponent1.id);
          const opponent2 = data.participant.find((p: any) => p.id === match.opponent2.id);
          // Validate that both participants were found
          if (!opponent1 || !opponent2) {
            toast.error('No se pudieron encontrar los participantes del partido');
            return;
          }
          setCurrentMatch({ match, opponent1, opponent2 });
          setModalOpen(true);
        },
        clear: true,
      }
    );
  }, [data, readonly]);

  const handleSubmitResult = async (result: MatchResult) => {
    if (!currentMatch) return;
    const resultForApi: Record<
      'set1' | 'set2' | 'supertiebreak',
      { winner: string | null; score: number[] | null; gameId: string }
    > = {
      set1: {
        gameId: result.set1.gameId,
        winner: result.set1.winner,
        score: result.set1.score,
      },
      set2: {
        gameId: result.set2.gameId,
        winner: result.set2.winner,
        score: result.set2.score,
      },
      supertiebreak: {
        gameId: result.supertiebreak.gameId,
        winner: result.supertiebreak.winner,
        score: result.supertiebreak.score,
      },
    };
    await clubApi.registerMatchResult(
      club.id,
      tournament.id,
      categoryId,
      currentMatch.match.id,
      resultForApi
    );
    revalidator.revalidate();
  };

  return (
    <>
      <div className="brackets-viewer"></div>
      {currentMatch && currentMatch.opponent1 && currentMatch.opponent2 && (
        <MatchResultModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          opponent1={{
            id: currentMatch.opponent1.id,
            name: currentMatch.opponent1.name || `Jugador ${currentMatch.opponent1.id}`,
          }}
          opponent2={{
            id: currentMatch.opponent2.id,
            name: currentMatch.opponent2.name || `Jugador ${currentMatch.opponent2.id}`,
          }}
          matchGames={currentMatch.match.metadata.games}
          onSubmit={handleSubmitResult}
        />
      )}
    </>
  );
};
